import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { addNotification } from '../../../store';
import { setDocumentsButtonsEnabled } from '../../../store/UI/projectStepper.slice';

import { useUpload } from '../../../hooks';
import { NgFileUpload } from '../../NgFileUpload/NgFileUpload';
import { IngestError, IngestStatus } from './components/IngestStatus';
import { useCreateIngestJobMutation } from '../../../services/ingest.service';

interface WorkbookUploadProps {
  workbookName?: string;
  targetId?: number;
  targetKey?: string;
  uploadFolderId: number;
  project?: any;
  onComplete: (data: IngestJob) => void;
  onError?: (data: any) => void;
}

const StyledContainer = styled.div`
  min-height: 240px;
  min-width: 240px;
  display: grid;
  grid-template-rows: 1fr;
`;

export const WorkbookUpload: FC<WorkbookUploadProps> = ({
  project,
  targetKey = 'project_id',
  targetId,
  uploadFolderId,
  onComplete,
  onError,
  workbookName,
}) => {
  const dispatch = useDispatch();
  const { uploadFiles } = useUpload();

  const [ingestId, setIngestId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [ingestMessage, setIngestMessage] = useState<string | null>(null);

  const [createIngestJob] = useCreateIngestJobMutation();

  const handleIngestStatusComplete = async (payload: IngestJob) => {
    dispatch(
      addNotification({
        type: 'success',
        message: payload?.user_status || 'Workbook has been successfully parsed.',
      }),
    );

    dispatch(setDocumentsButtonsEnabled(true));

    setIngestId(null);
    onComplete(payload);
    setIngestMessage(null);
  };

  const handleIngestError = (data: IngestError) => {
    dispatch(
      addNotification({
        type: 'error',
        message: data.error,
      }),
    );

    setIngestId(null);
    setIngestMessage(null);
  };

  const handleAssetUpload = async (data: any) => {
    setIsUploading(true);

    setIngestMessage('Uploading workbook.');

    const uploadResponse = await uploadFiles({ file: data, folderId: uploadFolderId });

    if (uploadResponse?.error || uploadResponse?.status === 'failed') {
      return;
    }

    const bucket = uploadResponse?.responseData?.Bucket;
    const key = uploadResponse?.responseData?.Key;

    if (!key || !bucket) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Failed to create job - upload response incomplete.',
        }),
      );
      console.error(
        `Failed to create job - upload response incomplete. Invalid upload response: missing ${
          !bucket ? 'bucket' : ''
        } ${!key ? 'key' : ''}`.trim(),
      );
      return;
    }

    setIngestMessage('Creating new job.');

    let ingestResponse;

    ingestResponse = await createIngestJob({
      key: key,
      file_name: key,
      bucket: bucket,
      [targetKey]: project?.ID,
    });

    //@ts-ignore
    const { ID } = ingestResponse?.data?.data;

    if (!ID) {
      const message = 'Could not start polling. No ingest job ID!';
      dispatch(
        addNotification({
          type: 'error',
          message: message,
        }),
      );
      console.error(message);
    }

    setIngestId(ID);
    setIsUploading(false);
  };

  return (
    <StyledContainer>
      {ingestId || ingestMessage ? (
        <IngestStatus
          ingestId={ingestId}
          message={ingestMessage}
          onComplete={handleIngestStatusComplete}
          onError={handleIngestError}
        />
      ) : (
        <NgFileUpload
          title={workbookName}
          targetKey={targetKey}
          targetId={targetId}
          disabled={isUploading}
          onUpload={handleAssetUpload}
          onError={onError}
        />
      )}
    </StyledContainer>
  );
};
